import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { LuCalendarClock } from "react-icons/lu";
import {
  MdAccessTime,
  MdCancel,
  MdExpandLess,
  MdExpandMore,
  MdLocalOffer,
  MdLocationOn,
  MdNotificationsActive,
  MdOutlineWork,
  MdPendingActions,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { resendJobNotificationAsync, updateJobStatusAsync } from "../../../services/services";
import { asyncStatus } from "../../../utils/asyncStatus";
import { themeOrange } from "../../../utils/colorTheme";
import { getServiceLocalImage } from "../../../utils/serviceImages";

const RESEND_DELAY_MS = 10 * 60 * 1000; // 10 minutes

// Hook: returns seconds remaining until resend is available (0 = ready)
const useResendCountdown = (createdAt) => {
  const getSecondsLeft = () => {
    const elapsed = Date.now() - new Date(createdAt).getTime();
    return Math.max(0, Math.ceil((RESEND_DELAY_MS - elapsed) / 1000));
  };
  const [secondsLeft, setSecondsLeft] = useState(getSecondsLeft);

  useEffect(() => {
    if (secondsLeft === 0) return;
    const interval = setInterval(() => {
      const s = getSecondsLeft();
      setSecondsLeft(s);
      if (s === 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [createdAt]);

  return secondsLeft;
};

// Module-level component — must NOT be defined inside another component
const ResendControl = ({ job, onResend, isResending, resendingJobId }) => {
  const secondsLeft = useResendCountdown(job.created_at);
  const isReady = secondsLeft === 0;
  const isSending = isResending && resendingJobId === job.id;
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      gap={1.5}
      sx={{
        p: 2,
        borderRadius: "10px",
        backgroundColor: isReady ? `${themeOrange}08` : "#fafafa",
        border: `1.5px solid ${isReady ? `${themeOrange}40` : "#ebebeb"}`,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <MdNotificationsActive size={18} color={isReady ? themeOrange : "#bbb"} />
        {isReady ? (
          <Typography sx={{ fontSize: "13px", color: "#555" }}>
            No vendor has accepted yet.{" "}
            <span style={{ fontWeight: "700", color: themeOrange }}>
              Resend notifications to pros?
            </span>
          </Typography>
        ) : (
          <Typography sx={{ fontSize: "13px", color: "#888" }}>
            Auto-resend notifications in{" "}
            <span style={{ fontWeight: "700", color: "#555", fontVariantNumeric: "tabular-nums" }}>
              {mm}:{ss}
            </span>
          </Typography>
        )}
      </Stack>

      {isReady && (
        <Tooltip title="Notify all available pros again" placement="top">
          <Button
            size="small"
            disabled={isSending}
            onClick={() => onResend(job.id)}
            startIcon={
              isSending
                ? <CircularProgress size={14} sx={{ color: "white" }} />
                : <MdNotificationsActive size={14} />
            }
            sx={{
              background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "700",
              fontSize: "13px",
              px: 2.5,
              py: 0.75,
              whiteSpace: "nowrap",
              boxShadow: "0 3px 8px rgba(241,90,36,0.25)",
              "&:hover": { background: "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)" },
              "&.Mui-disabled": { opacity: 0.6 },
            }}
          >
            {isSending ? "Sending…" : "Resend Notifications"}
          </Button>
        </Tooltip>
      )}
    </Stack>
  );
};

const WaitingJobsTabInner = () => {
  const dispatch = useDispatch();
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [resendingJobId, setResendingJobId] = useState(null);
  const autoResentRef = useRef(new Set());
  const theme = useTheme();

  const { get_all_jobs_data, resend_job_notification_status, update_jobs_status } = useSelector(
    (state) => state.serviceManage
  );

  const isResending = resend_job_notification_status === asyncStatus.LOADING;
  const isCancelling = update_jobs_status === asyncStatus.LOADING;
  const [cancellingJobId, setCancellingJobId] = useState(null);

  const handleCancel = (id) => {
    setCancellingJobId(id);
    dispatch(updateJobStatusAsync({ status: "Cancelled", id }));
  };

  const waitingJobs = get_all_jobs_data?.filter((job) => {
    const s = job?.status?.trim().toLowerCase();
    return s === "in-queue" || s === "pending";
  }) || [];

  // Auto-trigger resend once per job when 10 min elapses
  useEffect(() => {
    if (!waitingJobs.length) return;
    const timers = waitingJobs
      .filter((job) => {
        const elapsed = Date.now() - new Date(job.created_at).getTime();
        return elapsed < RESEND_DELAY_MS;
      })
      .map((job) => {
        const elapsed = Date.now() - new Date(job.created_at).getTime();
        const delay = RESEND_DELAY_MS - elapsed;
        return setTimeout(() => {
          if (!autoResentRef.current.has(job.id)) {
            autoResentRef.current.add(job.id);
            dispatch(resendJobNotificationAsync(job.id));
          }
        }, delay);
      });
    return () => timers.forEach(clearTimeout);
  }, [waitingJobs.length, dispatch]);

  const handleResend = (jobId) => {
    setResendingJobId(jobId);
    dispatch(resendJobNotificationAsync(jobId));
  };

  const handleViewDetails = (jobId) => {
    setSelectedJobId((prev) => (prev === jobId ? null : jobId));
  };

  const getInvoiceData = (job) => {
    const servicePrice = job?.service?.price || 0;
    const serviceAmount = job?.service_amount || 0;
    const tax = job?.tax || 0;
    const totalAmount = job?.total_amount || 0;
    const memberDiscount = job?.type === "Member" ? 25 : 0;

    let couponDiscount = 0;
    if (job?.coupon && job?.coupon_percentage > 0) {
      if (job?.coupon_type === 1) {
        const beforeCouponAmount = serviceAmount / (1 - job.coupon_percentage / 100);
        couponDiscount = beforeCouponAmount - serviceAmount;
      } else if (job?.coupon_type === 0) {
        couponDiscount = job.coupon_percentage;
      }
    }

    return {
      originalServicePrice: servicePrice,
      memberDiscount,
      couponDiscount,
      serviceAmount,
      tax,
      totalAmount,
      couponType: job?.coupon_type,
      addOnsTotal: job?.add_ons?.reduce((sum, addon) => sum + (addon.price || 0), 0) || 0,
    };
  };

  const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return null;
    return format(d, "EEE, MMM d, yyyy");
  };

  if (!waitingJobs.length) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }} spacing={2}>
        <Box sx={{ width: 80, height: 80, borderRadius: "50%", backgroundColor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <MdOutlineWork size={36} color="#ccc" />
        </Box>
        <Typography sx={{ color: "#aaa", fontWeight: "500", fontSize: "16px" }}>
          No jobs awaiting approval
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {waitingJobs
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((e) => {
          const isSelected = selectedJobId === e?.id;
          const serviceName = e?.service?.name || "Service";
          const dateStr = e?.date_time_lock?.date;
          const invoiceData = getInvoiceData(e);

          return (
            <Box
              key={e?.id}
              sx={{
                borderRadius: "14px",
                border: "1.5px solid #ffe0b2",
                backgroundColor: "white",
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(255,152,0,0.12)",
                transition: "box-shadow 0.2s",
              }}
            >
              {/* Approval waiting stripe */}
              <Box
                sx={{
                  px: 3,
                  py: 0.75,
                  background: "linear-gradient(90deg, #e65100 0%, #FF9800 100%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <MdPendingActions size={14} color="white" />
                <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "700", letterSpacing: "0.5px" }}>
                  AWAITING VENDOR APPROVAL
                </Typography>
              </Box>

              {/* Main card row */}
              <Stack direction={{ xs: "column", sm: "row" }} sx={{ p: 2.5 }} gap={2}>
                {/* Service image */}
                <Box
                  sx={{
                    width: { xs: "100%", sm: 110 },
                    height: { xs: 140, sm: 110 },
                    borderRadius: "10px",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={getServiceLocalImage(serviceName)}
                    alt={serviceName}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                {/* Content */}
                <Stack flex={1} spacing={1.5}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography sx={{ fontWeight: "700", fontSize: "17px", color: "#1a1a1a", lineHeight: 1.3 }}>
                      {serviceName}
                    </Typography>
                    {invoiceData.totalAmount > 0 && (
                      <Typography sx={{ fontWeight: "800", fontSize: "18px", color: "#e65100", flexShrink: 0 }}>
                        ${(+invoiceData.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    )}
                  </Stack>

                  <Stack spacing={0.75}>
                    {dateStr && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <LuCalendarClock size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#666" }}>
                          {formatDate(dateStr)}
                        </Typography>
                        {e?.date_time_lock?.shift && (
                          <Typography sx={{ fontSize: "12px", color: "#999" }}>
                            ({e.date_time_lock.shift.slice(0, 3)})
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {e?.time && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdAccessTime size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888" }}>
                          {e.time.length > 60 ? `${e.time.substring(0, 60)}...` : e.time}
                        </Typography>
                      </Stack>
                    )}
                    {e?.location && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdLocationOn size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "320px" }}>
                          {e.location}
                        </Typography>
                      </Stack>
                    )}
                    {e?.coupon && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdLocalOffer size={16} color="#4caf50" style={{ flexShrink: 0 }} />
                        <Chip
                          label={
                            e.coupon_type === 1
                              ? `${e.coupon} (${e.coupon_percentage}% OFF)`
                              : `${e.coupon} ($${e.coupon_percentage} OFF)`
                          }
                          size="small"
                          sx={{ backgroundColor: "#e8f5e8", color: "#4caf50", fontWeight: "600", fontSize: "11px" }}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Stack>

                {/* Right: status chip + expand toggle */}
                <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} justifyContent="space-between" flexShrink={0} gap={1}>
                  <Chip
                    label="In Queue"
                    size="small"
                    sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: "700", fontSize: "11px", border: "1px solid #ffcc80" }}
                  />
                  <Box
                    sx={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: `${themeOrange}10`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    onClick={() => handleViewDetails(e?.id)}
                  >
                    {isSelected ? <MdExpandLess size={20} color={themeOrange} /> : <MdExpandMore size={20} color={themeOrange} />}
                  </Box>
                </Stack>
              </Stack>

              {/* Expanded invoice / details section */}
              <Collapse in={isSelected}>
                <Box sx={{ px: 3, pb: 3, borderTop: "1px solid #f0f0f0", pt: 2.5 }}>
                  <Stack spacing={2.5}>

                    {e?.description && (
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>Job Description</Typography>
                        <Box sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: "10px", border: "1px solid #f0f0f0", borderLeft: `3px solid ${themeOrange}` }}>
                          <Typography sx={{ color: "#555", fontSize: "14px", lineHeight: 1.6 }}>{e.description}</Typography>
                        </Box>
                      </Stack>
                    )}

                    {e?.time && (
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>Timing Constraints</Typography>
                        <Box sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: "10px", border: "1px solid #f0f0f0" }}>
                          <Typography sx={{ color: "#555", fontSize: "14px" }}>{e.time}</Typography>
                        </Box>
                      </Stack>
                    )}

                    {e?.images?.length > 0 && (
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>Job Photos</Typography>
                        <Stack spacing={1}>
                          {e.images.map(({ url, caption }) => (
                            <Box key={url} sx={{ p: 1.5, border: "1px solid #f0f0f0", borderRadius: "10px", backgroundColor: "#fafafa" }}>
                              <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={3} sm={2}>
                                  <Box sx={{ height: 80, borderRadius: "8px", overflow: "hidden" }}>
                                    <img src={url} alt={caption} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  </Box>
                                </Grid>
                                <Grid item xs={9} sm={10}>
                                  <Typography sx={{ color: "#666", fontSize: "13px", lineHeight: 1.5 }}>{caption || ""}</Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </Stack>
                      </Stack>
                    )}

                    {/* Invoice */}
                    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#fafafa", borderRadius: "12px", border: "1px solid #f0f0f0" }}>
                      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: theme.palette.primary.main }}>
                          Invoice
                        </Typography>
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Date: {new Date(e?.created_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Invoice #: INV-{e?.id}
                          </Typography>
                          {e?.type === "Member" && (
                            <Typography variant="body2" color="textSecondary">Member Discount Applied</Typography>
                          )}
                          {e?.coupon && (
                            <Typography variant="body2" sx={{ color: "#4caf50", fontWeight: "600" }}>
                              Coupon: {e.coupon}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                              <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography fontWeight="bold">{e?.service?.name}</Typography>
                                <Typography variant="body2">{e?.service?.description}</Typography>
                              </TableCell>
                              <TableCell sx={{ textAlign: "right" }}>
                                ${invoiceData.originalServicePrice.toFixed(2)}
                              </TableCell>
                            </TableRow>

                            {invoiceData.memberDiscount > 0 && (
                              <TableRow>
                                <TableCell>
                                  <Typography fontStyle="italic" sx={{ color: theme.palette.info.main }}>
                                    Member Discount
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: "right", color: theme.palette.info.main }}>
                                  -${invoiceData.memberDiscount.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            )}

                            {e?.add_ons?.map((addon, index) => (
                              <TableRow key={index}>
                                <TableCell><Typography>Add-on: {addon.name}</Typography></TableCell>
                                <TableCell sx={{ textAlign: "right" }}>${addon.price.toFixed(2)}</TableCell>
                              </TableRow>
                            ))}

                            {e?.coupon && (
                              <TableRow>
                                <TableCell>
                                  <Typography fontStyle="italic" sx={{ color: "#4caf50" }}>
                                    Coupon ({e.coupon} - {e.coupon_type === 1 ? `${e.coupon_percentage}%` : `$${e.coupon_percentage}`})
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: "right", color: "#4caf50", fontWeight: "600" }}>
                                  -${invoiceData.couponDiscount.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            )}

                            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                              <TableCell><Typography fontWeight="600">Service Amount (after discounts)</Typography></TableCell>
                              <TableCell sx={{ textAlign: "right", fontWeight: "600" }}>
                                ${e.service_amount.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Grid container spacing={1} sx={{ maxWidth: 360, ml: "auto", mt: 2 }}>
                        <Grid item xs={8}>
                          <Typography variant="body1">Subtotal:</Typography>
                          <Typography variant="body1">Tax (13%):</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>Total:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body1">${e.service_amount.toFixed(2)}</Typography>
                          <Typography variant="body1">${e.tax.toFixed(2)}</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: "bold", color: theme.palette.success.main }}>
                            ${e.total_amount.toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {(invoiceData.memberDiscount > 0 || invoiceData.couponDiscount > 0) && (
                        <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f0fdf4", borderRadius: "8px", textAlign: "center" }}>
                          <Typography variant="body2" color="success.main" fontWeight="600">
                            You saved ${(invoiceData.memberDiscount + invoiceData.couponDiscount).toFixed(2)} on this booking!
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ mt: 2, mb: 1.5 }} />
                      <Typography variant="body2" sx={{ fontStyle: "italic", textAlign: "center", color: "#999" }}>
                        Thank you for your business. Payments payable to Jiff Done.
                      </Typography>
                    </Paper>

                    <Box sx={{ p: 2, backgroundColor: "#fff8e1", borderRadius: "10px", border: "1px solid #ffe082", display: "flex", alignItems: "center", gap: 1.5 }}>
                      <MdPendingActions size={20} color="#f57c00" style={{ flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "14px", color: "#e65100", fontWeight: "500" }}>
                        Waiting for a pro to accept this job.
                      </Typography>
                    </Box>

                  </Stack>
                </Box>
              </Collapse>

              {/* Resend notifications + Cancel row — always visible */}
              <Box sx={{ px: 2.5, pb: 2.5 }}>
                <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
                  <ResendControl
                    job={e}
                    onResend={handleResend}
                    isResending={isResending}
                    resendingJobId={resendingJobId}
                  />
                  <Button
                    variant="outlined"
                    disabled={isCancelling && cancellingJobId === e.id}
                    startIcon={
                      isCancelling && cancellingJobId === e.id
                        ? <CircularProgress size={14} sx={{ color: "#ef5350" }} />
                        : <MdCancel size={16} />
                    }
                    onClick={() => handleCancel(e?.id)}
                    sx={{
                      borderColor: "#ef5350",
                      color: "#ef5350",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "13px",
                      px: 2.5,
                      whiteSpace: "nowrap",
                      "&:hover": { backgroundColor: "#fef2f2", borderColor: "#ef5350" },
                    }}
                  >
                    {isCancelling && cancellingJobId === e.id ? "Cancelling…" : "Cancel Job"}
                  </Button>
                </Stack>
              </Box>
            </Box>
          );
        })}
    </Stack>
  );
};

const WaitingJobsTab = () => <WaitingJobsTabInner />;

export default WaitingJobsTab;
